import type { LangMap } from "./index";

/**
 * Override di etichette in base al profilo attivo: la stessa chiave assume un
 * nome diverso per docenti e famiglie (es. "Tutor AI" → "Assistente").
 */

const it: Record<string, unknown> = {
  switch: {
    title: "Profilo attivo",
    subtitle: "Puoi passare da un profilo all'altro quando vuoi.",
    switchTo: "Passa a {{role}}",
    switched: "Ora stai usando Lumen come {{role}}",
    add: "Aggiungi un secondo profilo",
    addHint: "Disponibile per i maggiorenni (es. professori che sono anche genitori).",
    adult: "Sono maggiorenne",
    max: "Puoi avere al massimo due profili.",
    minorHint: "Per avere due profili devi dichiarare di essere maggiorenne.",
    saved: "Profili aggiornati",
  },
  manage: {
    title: "Gestione profili",
    subtitle: "Aggiungi, rinomina o rimuovi i tuoi profili e scegli quale usare all'avvio.",
    open: "Gestione profili",
    active: "In uso ora",
    default: "Predefinito",
    setDefault: "Usa all'avvio",
    rename: "Rinomina",
    namePlaceholder: "Nome del profilo (es. Prof. Rossi)",
    save: "Salva",
    remove: "Rimuovi profilo",
    removeConfirm: "Rimuovere questo profilo? Le sue preferenze verranno cancellate.",
    lastProfile: "Devi mantenere almeno un profilo.",
    addTitle: "Aggiungi profilo",
    limit: "Limite raggiunto: puoi avere al massimo 2 profili.",
    minor: "Solo i maggiorenni possono avere due profili.",
    duplicate: "Hai già questo profilo.",
    prefsTitle: "Preferenze separate",
    prefsHint: "Lingua, accessibilità, stile di studio e notifiche vengono salvate per ogni profilo e riapplicate quando cambi.",
    denied: "Questa sezione non è disponibile per il profilo attivo.",
  },

  teacher: {
    "nav.ai": "Assistente",
    "nav.studio": "Contenuti",
    "nav.home": "Classi",
    "nav.stats": "Risultati",
    "nav.community": "Community docenti",
    "lumen.tutor.title": "Assistente didattico",
    "lumen.tutor.subtitle": "Ti aiuto a preparare lezioni, verifiche e materiali",
    "lumen.tutor.placeholder": "Chiedi una traccia, una verifica o una spiegazione…",
  },
  parent: {
    "nav.ai": "Assistente famiglia",
    "nav.home": "Famiglia",
    "nav.stats": "Andamento",
    "lumen.tutor.title": "Assistente famiglia",
    "lumen.tutor.subtitle": "Ti aiuto a capire l'andamento e a sostenere lo studio a casa",
    "lumen.tutor.placeholder": "Chiedi come sostenere lo studio a casa…",
  },
  student: {},
};

const en: Record<string, unknown> = {
  switch: {
    title: "Active profile",
    subtitle: "Switch between your profiles whenever you want.",
    switchTo: "Switch to {{role}}",
    switched: "You are now using Lumen as {{role}}",
    add: "Add a second profile",
    addHint: "Available to adults (e.g. teachers who are also parents).",
    adult: "I am 18 or older",
    max: "You can have at most two profiles.",
    minorHint: "To keep two profiles you must confirm you are an adult.",
    saved: "Profiles updated",
  },
  manage: {
    title: "Manage profiles",
    subtitle: "Add, rename or remove your profiles and pick the one to start with.",
    open: "Manage profiles",
    active: "In use now",
    default: "Default",
    setDefault: "Use at startup",
    rename: "Rename",
    namePlaceholder: "Profile name (e.g. Mr. Smith)",
    save: "Save",
    remove: "Remove profile",
    removeConfirm: "Remove this profile? Its preferences will be deleted.",
    lastProfile: "You must keep at least one profile.",
    addTitle: "Add profile",
    limit: "Limit reached: you can have at most 2 profiles.",
    minor: "Only adults can have two profiles.",
    duplicate: "You already have this profile.",
    prefsTitle: "Separate preferences",
    prefsHint: "Language, accessibility, study style and notifications are saved per profile and reapplied when you switch.",
    denied: "This section is not available for the active profile.",
  },

  teacher: {
    "nav.ai": "Assistant",
    "nav.studio": "Content",
    "nav.home": "Classes",
    "nav.stats": "Results",
    "nav.community": "Teacher community",
    "lumen.tutor.title": "Teaching assistant",
    "lumen.tutor.subtitle": "I help you plan lessons, tests and materials",
    "lumen.tutor.placeholder": "Ask for a lesson plan, a test or an explanation…",
  },
  parent: {
    "nav.ai": "Family assistant",
    "nav.home": "Family",
    "nav.stats": "Progress",
    "lumen.tutor.title": "Family assistant",
    "lumen.tutor.subtitle": "I help you follow progress and support study at home",
    "lumen.tutor.placeholder": "Ask how to support study at home…",
  },
  student: {},
};

const es: Record<string, unknown> = {
  switch: {
    title: "Perfil activo",
    subtitle: "Cambia de perfil cuando quieras.",
    switchTo: "Cambiar a {{role}}",
    switched: "Ahora usas Lumen como {{role}}",
    add: "Añadir un segundo perfil",
    addHint: "Disponible para mayores de edad (p. ej. profesores que también son padres).",
    adult: "Soy mayor de edad",
    max: "Puedes tener como máximo dos perfiles.",
    minorHint: "Para tener dos perfiles debes confirmar que eres mayor de edad.",
    saved: "Perfiles actualizados",
  },
  manage: {
    title: "Gestión de perfiles",
    subtitle: "Añade, renombra o elimina tus perfiles y elige con cuál empezar.",
    open: "Gestión de perfiles",
    active: "En uso ahora",
    default: "Predeterminado",
    setDefault: "Usar al iniciar",
    rename: "Renombrar",
    namePlaceholder: "Nombre del perfil (p. ej. Prof. García)",
    save: "Guardar",
    remove: "Eliminar perfil",
    removeConfirm: "¿Eliminar este perfil? Se borrarán sus preferencias.",
    lastProfile: "Debes conservar al menos un perfil.",
    addTitle: "Añadir perfil",
    limit: "Límite alcanzado: puedes tener como máximo 2 perfiles.",
    minor: "Solo los mayores de edad pueden tener dos perfiles.",
    duplicate: "Ya tienes este perfil.",
    prefsTitle: "Preferencias separadas",
    prefsHint: "Idioma, accesibilidad, estilo de estudio y notificaciones se guardan por perfil y se reaplican al cambiar.",
    denied: "Esta sección no está disponible para el perfil activo.",
  },
  teacher: {
    "nav.ai": "Asistente",
    "nav.studio": "Contenidos",
    "nav.home": "Clases",
    "nav.stats": "Resultados",
    "nav.community": "Comunidad docente",
    "lumen.tutor.title": "Asistente docente",
    "lumen.tutor.subtitle": "Te ayudo a preparar clases, pruebas y materiales",
    "lumen.tutor.placeholder": "Pide una programación, una prueba o una explicación…",
  },
  parent: {
    "nav.ai": "Asistente familiar",
    "nav.home": "Familia",
    "nav.stats": "Evolución",
    "lumen.tutor.title": "Asistente familiar",
    "lumen.tutor.subtitle": "Te ayudo a entender la evolución y a apoyar el estudio en casa",
    "lumen.tutor.placeholder": "Pregunta cómo apoyar el estudio en casa…",
  },
  student: {},
};

const fr: Record<string, unknown> = {
  switch: {
    title: "Profil actif",
    subtitle: "Change de profil quand tu veux.",
    switchTo: "Passer à {{role}}",
    switched: "Tu utilises maintenant Lumen en tant que {{role}}",
    add: "Ajouter un second profil",
    addHint: "Réservé aux majeurs (par ex. les enseignants aussi parents).",
    adult: "Je suis majeur·e",
    max: "Tu peux avoir deux profils au maximum.",
    minorHint: "Pour avoir deux profils, tu dois confirmer que tu es majeur·e.",
    saved: "Profils mis à jour",
  },
  manage: {
    title: "Gestion des profils",
    subtitle: "Ajoute, renomme ou supprime tes profils et choisis celui du démarrage.",
    open: "Gestion des profils",
    active: "Utilisé actuellement",
    default: "Par défaut",
    setDefault: "Utiliser au démarrage",
    rename: "Renommer",
    namePlaceholder: "Nom du profil (par ex. M. Dupont)",
    save: "Enregistrer",
    remove: "Supprimer le profil",
    removeConfirm: "Supprimer ce profil ? Ses préférences seront effacées.",
    lastProfile: "Tu dois garder au moins un profil.",
    addTitle: "Ajouter un profil",
    limit: "Limite atteinte : 2 profils au maximum.",
    minor: "Seuls les majeurs peuvent avoir deux profils.",
    duplicate: "Tu as déjà ce profil.",
    prefsTitle: "Préférences séparées",
    prefsHint: "Langue, accessibilité, style de révision et notifications sont enregistrés par profil et réappliqués au changement.",
    denied: "Cette section n'est pas disponible pour le profil actif.",
  },
  teacher: {
    "nav.ai": "Assistant",
    "nav.studio": "Contenus",
    "nav.home": "Classes",
    "nav.stats": "Résultats",
    "nav.community": "Communauté enseignante",
    "lumen.tutor.title": "Assistant pédagogique",
    "lumen.tutor.subtitle": "Je t'aide à préparer cours, évaluations et supports",
    "lumen.tutor.placeholder": "Demande un plan de cours, une évaluation ou une explication…",
  },
  parent: {
    "nav.ai": "Assistant famille",
    "nav.home": "Famille",
    "nav.stats": "Évolution",
    "lumen.tutor.title": "Assistant famille",
    "lumen.tutor.subtitle": "Je t'aide à suivre les progrès et à soutenir le travail à la maison",
    "lumen.tutor.placeholder": "Demande comment accompagner le travail à la maison…",
  },
  student: {},
};

const de: Record<string, unknown> = {
  switch: {
    title: "Aktives Profil",
    subtitle: "Wechsle jederzeit zwischen deinen Profilen.",
    switchTo: "Zu {{role}} wechseln",
    switched: "Du nutzt Lumen jetzt als {{role}}",
    add: "Zweites Profil hinzufügen",
    addHint: "Für Volljährige (z. B. Lehrkräfte, die auch Eltern sind).",
    adult: "Ich bin volljährig",
    max: "Du kannst höchstens zwei Profile haben.",
    minorHint: "Für zwei Profile musst du bestätigen, dass du volljährig bist.",
    saved: "Profile aktualisiert",
  },
  manage: {
    title: "Profile verwalten",
    subtitle: "Profile hinzufügen, umbenennen oder entfernen und das Startprofil wählen.",
    open: "Profile verwalten",
    active: "Aktuell aktiv",
    default: "Standard",
    setDefault: "Beim Start verwenden",
    rename: "Umbenennen",
    namePlaceholder: "Profilname (z. B. Frau Meier)",
    save: "Speichern",
    remove: "Profil entfernen",
    removeConfirm: "Dieses Profil entfernen? Seine Einstellungen werden gelöscht.",
    lastProfile: "Mindestens ein Profil muss bleiben.",
    addTitle: "Profil hinzufügen",
    limit: "Limit erreicht: höchstens 2 Profile.",
    minor: "Nur Volljährige können zwei Profile haben.",
    duplicate: "Dieses Profil hast du schon.",
    prefsTitle: "Getrennte Einstellungen",
    prefsHint: "Sprache, Barrierefreiheit, Lernstil und Benachrichtigungen werden pro Profil gespeichert und beim Wechsel wieder angewendet.",
    denied: "Dieser Bereich ist für das aktive Profil nicht verfügbar.",
  },
  teacher: {
    "nav.ai": "Assistent",
    "nav.studio": "Inhalte",
    "nav.home": "Klassen",
    "nav.stats": "Ergebnisse",
    "nav.community": "Lehrkräfte-Community",
    "lumen.tutor.title": "Unterrichtsassistent",
    "lumen.tutor.subtitle": "Ich helfe dir bei Stunden, Tests und Materialien",
    "lumen.tutor.placeholder": "Frag nach einer Stundenplanung, einem Test oder einer Erklärung…",
  },
  parent: {
    "nav.ai": "Familien-Assistent",
    "nav.home": "Familie",
    "nav.stats": "Entwicklung",
    "lumen.tutor.title": "Familien-Assistent",
    "lumen.tutor.subtitle": "Ich helfe dir, die Entwicklung zu verstehen und zu Hause zu unterstützen",
    "lumen.tutor.placeholder": "Frag, wie du das Lernen zu Hause unterstützen kannst…",
  },
  student: {},
};

const pt: Record<string, unknown> = {
  switch: {
    title: "Perfil ativo",
    subtitle: "Muda de perfil quando quiseres.",
    switchTo: "Mudar para {{role}}",
    switched: "Estás agora a usar o Lumen como {{role}}",
    add: "Adicionar um segundo perfil",
    addHint: "Disponível para maiores de idade (ex.: professores que também são pais).",
    adult: "Sou maior de idade",
    max: "Podes ter no máximo dois perfis.",
    minorHint: "Para ter dois perfis tens de confirmar que és maior de idade.",
    saved: "Perfis atualizados",
  },
  manage: {
    title: "Gestão de perfis",
    subtitle: "Adiciona, renomeia ou remove perfis e escolhe qual usar no arranque.",
    open: "Gestão de perfis",
    active: "Em uso agora",
    default: "Predefinido",
    setDefault: "Usar no arranque",
    rename: "Renomear",
    namePlaceholder: "Nome do perfil (ex.: Prof. Silva)",
    save: "Guardar",
    remove: "Remover perfil",
    removeConfirm: "Remover este perfil? As preferências serão apagadas.",
    lastProfile: "Tens de manter pelo menos um perfil.",
    addTitle: "Adicionar perfil",
    limit: "Limite atingido: no máximo 2 perfis.",
    minor: "Só maiores de idade podem ter dois perfis.",
    duplicate: "Já tens este perfil.",
    prefsTitle: "Preferências separadas",
    prefsHint: "Idioma, acessibilidade, estilo de estudo e notificações são guardados por perfil e reaplicados ao mudar.",
    denied: "Esta secção não está disponível para o perfil ativo.",
  },
  teacher: {
    "nav.ai": "Assistente",
    "nav.studio": "Conteúdos",
    "nav.home": "Turmas",
    "nav.stats": "Resultados",
    "nav.community": "Comunidade docente",
    "lumen.tutor.title": "Assistente docente",
    "lumen.tutor.subtitle": "Ajudo-te a preparar aulas, testes e materiais",
    "lumen.tutor.placeholder": "Pede uma planificação, um teste ou uma explicação…",
  },
  parent: {
    "nav.ai": "Assistente família",
    "nav.home": "Família",
    "nav.stats": "Evolução",
    "lumen.tutor.title": "Assistente família",
    "lumen.tutor.subtitle": "Ajudo-te a acompanhar a evolução e a apoiar o estudo em casa",
    "lumen.tutor.placeholder": "Pergunta como apoiar o estudo em casa…",
  },
  student: {},
};

export const roleui: LangMap = { it, en, es, fr, de, pt };
